import { Fragment, type ReactElement } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Divider, NavLink as MantineNavLink, ScrollArea, Select, Text } from '@mantine/core';
import type { LucideIcon } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import type { FeatureNavGroup, FeatureNavItem } from './FeatureNavTypes';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-16px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const SubNavColumn = styled.div`
    width: 240px;
    flex-shrink: 0;
    background: var(--bm-color-body);
    border-right: 1px solid var(--bm-color-border);
    height: 100%;
    animation: ${slideIn} 250ms ease both;
`;

interface FeatureNavLayoutProps {
    /** Rail title header and Select fallback group label. */
    title: string;
    /** Accessible label for the mobile Select. */
    ariaLabel: string;
    /** The nav content, top to bottom. */
    groups: FeatureNavGroup[];
    /** Value exposed to child screens via `useOutletContext()` — e.g. a layout-owned ViewModel shared with a screen. */
    outletContext?: unknown;
}

/**
 * The shared feature-shell chrome: a full-height 240px interior sub-nav column
 * (its screens grouped, with optional section headers and dividers) beside a
 * scrollable content area that renders the active screen through an `<Outlet />`.
 * On mobile the rail collapses to a grouped Select above the content. Every
 * analytics feature (Behavior, Token Usage, Cost, Adoption, Governance) and
 * Settings render their nav through this one component — see
 * `docs/adding-a-feature.md`.
 */
export default function FeatureNavLayout({ title, ariaLabel, groups, outletContext }: FeatureNavLayoutProps): ReactElement {
    const isDesktop = useBreakpointUp('sm');
    const frameHeight = isDesktop ? 'calc(100vh - 56px)' : 'calc(100dvh - 56px)';

    return (
        <Box style={{ display: 'flex', height: frameHeight }}>
            {isDesktop && (
                <SubNavColumn className="bm-subrail">
                    <FeatureSubNav title={title} ariaLabel={ariaLabel} groups={groups} />
                </SubNavColumn>
            )}

            <Box style={{ flex: 1, minWidth: 0 }}>
                <ScrollArea
                    h={frameHeight}
                    type="hover"
                    scrollbarSize={8}
                    styles={{ thumb: { backgroundColor: 'var(--mantine-color-zinc-3)', borderRadius: 4 } }}
                >
                    {!isDesktop && (
                        <Box px="md" pt="md">
                            <FeatureSubNav title={title} ariaLabel={ariaLabel} groups={groups} variant="select" />
                        </Box>
                    )}
                    <Box p="md" px={{ base: 'md', sm: 48 }}>
                        <Box maw={1400}>
                            <Outlet context={outletContext} />
                        </Box>
                    </Box>
                </ScrollArea>
            </Box>
        </Box>
    );
}

interface FeatureSubNavProps {
    title: string;
    ariaLabel: string;
    groups: FeatureNavGroup[];
    /** `rail` (default) renders the desktop column; `select` renders a compact dropdown for mobile. */
    variant?: 'rail' | 'select';
}

function FeatureSubNav({ title, ariaLabel, groups, variant = 'rail' }: FeatureSubNavProps): ReactElement {
    const location = useLocation();
    const navigate = useNavigate();

    const allItems = groups.flatMap((group) => group.items);
    const activePath =
        allItems
            .flatMap((item) => [item.path, ...(item.matchPaths ?? [])])
            .filter((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
            .sort((a, b) => b.length - a.length)[0] ?? null;
    const activeItem = activePath === null ? null : allItems.find((item) => item.path === activePath || (item.matchPaths?.includes(activePath) ?? false)) ?? null;
    const isActive = (item: FeatureNavItem): boolean => item === activeItem;

    if (variant === 'select') {
        const data = groups.map((group) => ({
            group: group.selectGroupLabel ?? group.header?.label ?? title,
            items: group.items.map((item) => ({ value: item.path, label: item.label })),
        }));
        return (
            <Select
                aria-label={ariaLabel}
                data={data}
                value={activeItem?.path ?? null}
                onChange={(value) => {
                    if (value !== null) navigate(value);
                }}
                comboboxProps={{ withinPortal: true }}
                allowDeselect={false}
            />
        );
    }

    const navLink = (item: FeatureNavItem): ReactElement => {
        const Icon = item.icon;
        const active = isActive(item);
        return (
            <MantineNavLink
                key={item.path}
                label={item.label}
                leftSection={<Icon size={16} strokeWidth={active ? 2.5 : 2} />}
                rightSection={item.rightSection}
                active={active}
                onClick={() => navigate(item.path)}
                variant="light"
                mb={2}
                styles={{ label: { fontWeight: active ? 700 : 500, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
        );
    };

    return (
        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box px={22} pt={38} pb={34}>
                <Text size="sm" fw={700} style={{ color: 'var(--bm-color-text)' }}>
                    {title}
                </Text>
            </Box>

            <Box px="sm" style={{ flex: 1 }}>
                {groups.map((group, index) => (
                    <Fragment key={index}>
                        {group.dividerBefore === true && <Divider my="sm" color="var(--bm-color-border)" />}
                        {group.header !== undefined && <SectionHeader icon={group.header.icon} label={group.header.label} />}
                        {group.items.map(navLink)}
                    </Fragment>
                ))}
            </Box>
        </Box>
    );
}

function SectionHeader({ icon: Icon, label }: { icon: LucideIcon; label: string }): ReactElement {
    return (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }} mb={4} pl={12}>
            <Icon size={11} style={{ color: 'var(--bm-color-text-dimmed)' }} />
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                {label}
            </Text>
        </Box>
    );
}
